// A market's trading calendar and intraday shape. Analytics take one of these instead of
// assuming NSE, so adding NYSE or LSE is a new definition, not a rewrite.

export interface MarketSpec {
  id: string;
  name: string;
  timeZone: string;
  /** Session open/close in minutes after local midnight. */
  openMinute: number;
  closeMinute: number;
  barMinutes: number;
  currency: string;
  currencySymbol: string;
  locale: string;
  /** Relative volume by bar index (heavy open, quiet midday, pickup into the close). */
  volumeShape: (i: number, bars: number) => number;
}

export interface Market extends MarketSpec {
  /** Bars in a full session. */
  bars: number;
  volumeCurve: (i: number) => number;
  curveTotal: number;
  /** Clock label at the end of bar i (bar 0 → open time). */
  timeLabel: (i: number) => string;
  formatPrice: (v: number) => string;
}

export function defineMarket(spec: MarketSpec): Market {
  const bars = (spec.closeMinute - spec.openMinute) / spec.barMinutes;
  const volumeCurve = (i: number) => spec.volumeShape(i, bars);
  let curveTotal = 0;
  for (let i = 0; i < bars; i++) curveTotal += volumeCurve(i);
  const priceFmt = new Intl.NumberFormat(spec.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return {
    ...spec,
    bars,
    volumeCurve,
    curveTotal,
    timeLabel(i) {
      const m = spec.openMinute + i * spec.barMinutes;
      return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
    },
    formatPrice: (v) => priceFmt.format(v),
  };
}

/** Fraction of a typical day's volume that has traded by the end of bar t. */
export function curveFraction(market: Market, t: number): number {
  let curve = 0;
  for (let i = 0; i < t; i++) curve += market.volumeCurve(i);
  return curve / market.curveTotal;
}
